import { APPLICATION_STATE } from '@apps/contexts/ApplicationContext'
import { isSafeInternalPath } from '@apps/utils/navigation'
import cookie from 'react-cookies'
import { LANGUAGES_CODE } from '@apps/utils/constants'
import config from '@/config'

const KEY_LANG = 'lang'
const KEY_REGION = 'prov'

const langOptions = ['en', 'fr']
const provOptions = ['ab', 'bc', 'mb', 'nb', 'nl', 'nt', 'ns', 'nu', 'on', 'pe', 'qc', 'sk', 'yt']

const { cookieSecure } = config

/**
 * Swaps the leading `/<lang>/<prov>/` segment of an internal path so a stored
 * path keeps pointing at the page the user is actually on after they change
 * language or region from the header.
 */
const relocalizePath = (path, { lang, prov } = {}) => {
  if (typeof path !== 'string' || !path.startsWith('/')) return path

  const parts = path.split('/')
  // parts[0] is the empty string before the leading slash
  if (lang && langOptions.includes(parts[1]?.toLowerCase())) parts[1] = lang
  if (prov && provOptions.includes(parts[2]?.toLowerCase())) parts[2] = prov

  return parts.join('/')
}

/**
 * `history.push(path)` drops `location.state`, and pages such as the plans page
 * read `state.from` to decide where their "go back to previous step" link points
 * — losing it sent the user to the phone number fallback instead of the page
 * they came from. Carry `from` across and relocalize it so the back link stays
 * in the newly selected language/region.
 *
 * Only `from` is carried, because it is a path we can relocalize. Any other
 * state key is dropped as it was before: the splash route's `state.error`, for
 * one, holds copy the MFE already rendered in the outgoing language, and
 * ErrorSection falls back to its localized Contentful body without it. A new
 * state key that should survive a locale switch has to be added here.
 */
const relocalizeState = (state, locale) => {
  if (!state || typeof state !== 'object' || Array.isArray(state)) return undefined
  if (!isSafeInternalPath(state.from)) return undefined

  return { from: relocalizePath(state.from, locale) }
}

const pushLocalized = (history, path, state) => {
  if (state === undefined) {
    history.push(path)
    return
  }

  history.push(path, state)
}

export const changeLang = (newLang, location, history, updateAppState) => {
  const pathParts = location.pathname.split('/').filter((item) => item.length > 0)

  const currentLanguage = pathParts[0]
  document.documentElement.lang = newLang // setting the new language selected in HTML tag
  if (newLang === 'en') {
    // setting title tag
    document.title = 'Activate account'
  } else {
    document.title = 'activer le compte'
  }

  const newPath = location.pathname.replace(`/${currentLanguage.toLowerCase()}/`, `/${newLang}/`)
  pushLocalized(history, newPath, relocalizeState(location.state, { lang: newLang }))

  updateAppState(newLang)

  cookie.save(KEY_LANG, newLang, { path: '/', secure: cookieSecure })
}

export const changeRegion = (newRegion, location, history, setAppState, appState) => {
  const pathParts = location.pathname.split('/').filter((item) => item.length > 0)

  const currentRegion = pathParts[1]

  const newProv = newRegion.toLowerCase() === 'nf' ? 'nl' : newRegion.toLowerCase()
  const newPath = location.pathname.replace(
    `/${currentRegion.toLowerCase()}/`,
    `/${newProv}/`
  )
  pushLocalized(history, newPath, relocalizeState(location.state, { prov: newProv }))
  setAppState({
    ...appState,
    [APPLICATION_STATE.CURRENT_REGION]: newRegion,
    [APPLICATION_STATE.SHOW_SELECT_LANGUAGE]: false
  })

  cookie.save(KEY_REGION, newRegion, { path: '/', secure: cookieSecure })
}

export const getLocaleCookies = (req, res) => {
  const currentLang = req.universalCookies.get(KEY_LANG)?.toLowerCase()
  const currentRegion = req.universalCookies.get(KEY_REGION)?.toLowerCase()

  if (
    currentLang &&
    langOptions.includes(currentLang) &&
    currentRegion &&
    provOptions.includes(currentRegion)
  ) {
    return {
      lang: currentLang,
      prov: currentRegion
    }
  }

  const defaultLocale = {
    lang: LANGUAGES_CODE.EN,
    prov: 'on'
  }
  // set the response cookies for defaultLocale
  res.cookie(KEY_LANG, defaultLocale.lang, { path: '/', secure: cookieSecure })
  res.cookie(KEY_REGION, defaultLocale.prov, { path: '/', secure: cookieSecure })

  return defaultLocale
}

export const getLocale = (location) => {
  if (!location) return {}

  const pathParts = location.split('/').filter((item) => item.length > 0)

  const pathLang = pathParts[0] ? pathParts[0].toLowerCase() : undefined
  const pathProv = pathParts[1] ? pathParts[1].toLowerCase() : undefined

  if (
    pathParts.length > 0 &&
    langOptions.indexOf(pathLang) !== -1 &&
    provOptions.indexOf(pathProv) !== -1
  ) {
    cookie.save(KEY_LANG, pathLang, { path: '/', secure: cookieSecure })
    cookie.save(KEY_REGION, pathProv, { path: '/', secure: cookieSecure })
    return {
      lang: pathLang,
      prov: pathProv
    }
  }

  return {
    lang: LANGUAGES_CODE.EN,
    prov: 'on'
  }
}
